﻿import { DataGrid } from "@serenity-is/corelib";
import { trimToNull, tryFirst } from "@serenity-is/corelib/q";
import { GroupInfo } from "@serenity-is/corelib/slick";
import { Column, Group, GroupItemMetadataProvider } from "@serenity-is/sleekgrid";
import { DraggableGroupingPlugin, GroupChangeInfo } from "./DraggableGroupingPlugin";

export interface GridPersistanceFlags {
    groupColumns?: boolean;
}

export interface PersistedGridSettings {
    groupColumns?: string[];
}

export interface DraggableGroupingMixinOptions {
    grid: DataGrid<any, any>;
    onGroupChanged?: (info: GroupChangeInfo) => void;
    getCount?: (group: Group<any>) => number;
    groupItemMetadataProvider?: GroupItemMetadataProvider;
    displayTotalsRow?: boolean;
    collapsed?: boolean;
}

function sortBy(field: string, reverse?: boolean, primer?: (value: any) => any) {

    var key = function (x: any) {
        return primer ? primer(x[field]) : x[field]
    };

    return function (a: any, b: any) {
        var A = key(a), B = key(b);
        return (A < B ? -1 : (A > B ? 1 : 0)) * [1, -1][+!!reverse];
    }
};

export class DraggableGroupingMixin {

    public readonly plugin: DraggableGroupingPlugin;

    constructor(options: DraggableGroupingMixinOptions) {
        var dg = options.grid;

        var groupItemMetadataProvider = options.groupItemMetadataProvider ??
            dg.slickGrid?.getPluginByName("GroupItemMetadataProvider") as GroupItemMetadataProvider ??
            dg.view?.getGroupItemMetadataProvider?.();

        if (groupItemMetadataProvider == null) {
            groupItemMetadataProvider = new GroupItemMetadataProvider();
            if (dg.slickGrid)
                dg.slickGrid.registerPlugin(groupItemMetadataProvider);
        }

        if (dg.slickGrid && !dg.slickGrid.getOptions()?.groupingPanel) {
            dg.slickGrid.setOptions({ groupingPanel: true });
        }

        if (dg.view?.setGroupItemMetadataProvider)
            dg.view.setGroupItemMetadataProvider(groupItemMetadataProvider);

        var plugin = this.plugin = new DraggableGroupingPlugin({
            getAllColumns: () => (dg as any).allColumns,
            getCount: options.getCount,
            displayTotalsRow: options.displayTotalsRow,
            collapsed: options.collapsed
        });

        dg.slickGrid.registerPlugin(plugin);

        plugin.onGroupChanged.subscribe((e, args) => {
            if (args.userChange && !(dg as any).restoringSettings) {
                (dg as any).persistSettings(null);

                if (options.onGroupChanged)
                    options.onGroupChanged(args);
            }
        });

        var oldCurrentSettings = (dg as any).getCurrentSettings;
        (dg as any).getCurrentSettings = function (flags: GridPersistanceFlags) {
            var settings = oldCurrentSettings.apply(dg, [flags]);

            if (flags == null || flags.groupColumns == null || flags.groupColumns) {
                var grouping = (dg.view as any).getGrouping() as GroupInfo<any>[] || [];
                settings['groupColumns'] = grouping.map(x => x.getter)
                    .filter(x => x != null && typeof x == "string");
            }

            return settings;
        };

        function loadGroupCols(groupColsToLoad: string[]) {
            if (groupColsToLoad == null)
                return;

            var columns = (dg as any).allColumns as Column[];

            var groupCols = groupColsToLoad
                .map(x => tryFirst(columns, z => z.id == x))
                .filter(x => x != null);

            var groupings = groupCols.map((e: Column) => {
                if (e['grouping'] == null)
                    e['grouping'] = plugin.getGroupingFor(e);

                return e['grouping'];
            });

            dg.view.setGrouping(groupings as any);
            plugin.setDroppedGroups(groupCols.map(x => x.id));
            dg.slickGrid.invalidate();
        };

        function getDefaultGroupCols() {
            var colsWithOrder = (dg as any).allColumns.filter((x: Column) => x.sourceItem != null &&
                x.sourceItem.groupOrder != null) as Column[];
            if (colsWithOrder.length) {
                return colsWithOrder.sort(sortBy('groupOrder')).map(x => x.id);
            }
            return null;
        }

        var oldRestoreSettings = (dg as any).restoreSettings;
        (dg as any).restoreSettings = function (settings?: PersistedGridSettings, flags?: GridPersistanceFlags) {
            (dg as any).restoringSettings++;
            try {
                oldRestoreSettings.apply(dg, [settings, flags]);

                if (!(flags == null || flags.groupColumns == null || flags.groupColumns))
                    return;

                var groupColsToLoad: string[] = null;

                if (settings == null) {
                    var storage = this.getPersistanceStorage();
                    if (storage != null) {
                        var json = trimToNull(storage.getItem(this.getPersistanceKey()));
                        if (json) {
                            groupColsToLoad = (JSON.parse(json) || {}).groupColumns;
                        }
                    }
                }
                else
                    groupColsToLoad = settings.groupColumns;

                if (groupColsToLoad == null) {
                    groupColsToLoad = getDefaultGroupCols();
                }

                loadGroupCols(groupColsToLoad);
            }
            finally {
                (dg as any).restoringSettings--;
            }
        };
    }
}